/**
 * Tidshjelpere. Lagring i UTC, gruppering av døgn i Europe/Oslo.
 * Den lokale dagen kan være 23 eller 25 timer ved sommertidsskifte; vi antar aldri 24.
 */

export const TZ = 'Europe/Oslo';

const dtf = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

export interface LocalParts {
  y: number;
  m: number;
  d: number;
  h: number;
  mi: number;
  s: number;
}

export function osloParts(d: Date): LocalParts {
  const parts = dtf.formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  let h = get('hour');
  if (h === 24) h = 0;
  return { y: get('year'), m: get('month'), d: get('day'), h, mi: get('minute'), s: get('second') };
}

/** Offset i minutter mellom Oslo-lokaltid og UTC for det gitte tidspunktet (f.eks. 120 om sommeren). */
export function osloOffsetMinutes(d: Date): number {
  const p = osloParts(d);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
  return Math.round((asUtc - d.getTime()) / 60000);
}

/** Konverterer en Oslo-lokaltid til UTC-instant. */
export function osloLocalToUtc(y: number, m: number, d: number, h = 0, mi = 0): Date {
  const guess = Date.UTC(y, m - 1, d, h, mi);
  const off1 = osloOffsetMinutes(new Date(guess));
  let t = guess - off1 * 60000;
  const off2 = osloOffsetMinutes(new Date(t));
  if (off2 !== off1) t = guess - off2 * 60000;
  return new Date(t);
}

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** 'YYYY-MM-DD' for Oslo-dagen som instansen tilhører. */
export function osloDateOf(iso: string | Date): string {
  const p = osloParts(typeof iso === 'string' ? new Date(iso) : iso);
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`;
}

/** 'HH:MM' Oslo-lokaltid. */
export function osloTimeOf(iso: string | Date): string {
  const p = osloParts(typeof iso === 'string' ? new Date(iso) : iso);
  return `${pad2(p.h)}:${pad2(p.mi)}`;
}

export function parseDate(date: string): { y: number; m: number; d: number } {
  const [y, m, d] = date.split('-').map(Number);
  return { y, m, d };
}

/** Halvåpent UTC-intervall for en Oslo-dag. */
export function osloDayBoundsUtc(date: string): { fromUtc: string; toUtc: string } {
  const { y, m, d } = parseDate(date);
  const next = addDays(date, 1);
  const n = parseDate(next);
  return {
    fromUtc: osloLocalToUtc(y, m, d, 0, 0).toISOString(),
    toUtc: osloLocalToUtc(n.y, n.m, n.d, 0, 0).toISOString(),
  };
}

/** Intervall [fromDate 00:00, toDate+1 00:00) i Oslo, uttrykt i UTC. */
export function osloRangeUtc(fromDate: string, toDate: string): { fromUtc: string; toUtc: string } {
  return { fromUtc: osloDayBoundsUtc(fromDate).fromUtc, toUtc: osloDayBoundsUtc(toDate).toUtc };
}

export function osloLocalIso(date: string, h: number, mi = 0): string {
  const { y, m, d } = parseDate(date);
  return osloLocalToUtc(y, m, d, h, mi).toISOString();
}

/** Legger til n dager på en 'YYYY-MM-DD'-dato (kalenderaritmetikk). */
export function addDays(date: string, n: number): string {
  const { y, m, d } = parseDate(date);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

export function daysBetweenInclusive(from: string, to: string): number {
  const a = parseDate(from);
  const b = parseDate(to);
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86400000) + 1;
}

export function eachDay(from: string, to: string): string[] {
  const out: string[] = [];
  let cur = from;
  let guard = 0;
  while (cur <= to && guard < 1000) {
    out.push(cur);
    cur = addDays(cur, 1);
    guard++;
  }
  return out;
}

/** 0 = søndag ... 6 = lørdag (kalenderdato, uavhengig av tidssone). */
export function weekdayOf(date: string): number {
  const { y, m, d } = parseDate(date);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function hoursBetween(fromIso: string, toIso: string): number {
  return (new Date(toIso).getTime() - new Date(fromIso).getTime()) / 3600000;
}

/** Overlapp i timer mellom to halvåpne intervaller. */
export function overlapHours(aFrom: string, aTo: string, bFrom: string, bTo: string): number {
  const s = Math.max(new Date(aFrom).getTime(), new Date(bFrom).getTime());
  const e = Math.min(new Date(aTo).getTime(), new Date(bTo).getTime());
  return e > s ? (e - s) / 3600000 : 0;
}

export function overlaps(aFrom: string, aTo: string, bFrom: string, bTo: string): boolean {
  return aFrom < bTo && bFrom < aTo;
}

/** Forrige like lange kalenderperiode. */
export function previousPeriod(fromDate: string, toDate: string): { fromDate: string; toDate: string } {
  const len = daysBetweenInclusive(fromDate, toDate);
  return { fromDate: addDays(fromDate, -len), toDate: addDays(fromDate, -1) };
}

export function isValidDate(s: string | null | undefined): s is string {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const { y, m, d } = parseDate(s);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

export function isIsoUtc(s: string): boolean {
  if (typeof s !== 'string') return false;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?Z$/.test(s)) return false;
  return !Number.isNaN(new Date(s).getTime());
}

const nbDate = new Intl.DateTimeFormat('nb-NO', { timeZone: TZ, day: 'numeric', month: 'long', year: 'numeric' });
const nbDateShort = new Intl.DateTimeFormat('nb-NO', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric' });
const nbDateTime = new Intl.DateTimeFormat('nb-NO', {
  timeZone: TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** '2. oktober 2026' for en 'YYYY-MM-DD'. */
export function formatDateLong(date: string): string {
  const { y, m, d } = parseDate(date);
  return nbDate.format(new Date(Date.UTC(y, m - 1, d, 12)));
}

export function formatDateShort(date: string): string {
  const { y, m, d } = parseDate(date);
  return nbDateShort.format(new Date(Date.UTC(y, m - 1, d, 12)));
}

const nbWeekday = new Intl.DateTimeFormat('nb-NO', { timeZone: TZ, weekday: 'long' });

/** «tirsdag 27.10.2026» – ukedag + entydig kortdato, uavhengig av nettleserspråk. */
export function formatDateWeekday(date: string): string {
  const { y, m, d } = parseDate(date);
  return `${nbWeekday.format(new Date(Date.UTC(y, m - 1, d, 12)))} ${formatDateShort(date)}`;
}

/**
 * Tolker brukerskrevet klokkeslett til 'HH:MM' (24-timers). Godtar «7», «07», «730», «0730», «7:30», «7.30», «7,30».
 * Returnerer null når det ikke er et gyldig klokkeslett. Aldri AM/PM.
 */
export function normalizeTime(raw: string | null | undefined): string | null {
  const s = String(raw ?? '')
    .trim()
    .replace(/\s+/g, '');
  if (!s) return null;
  let h: number;
  let mi: number;
  const sep = /^(\d{1,2})[:.,hH](\d{1,2})$/.exec(s);
  if (sep) {
    h = Number(sep[1]);
    mi = Number(sep[2]);
  } else if (/^\d{3,4}$/.test(s)) {
    h = Number(s.slice(0, s.length - 2));
    mi = Number(s.slice(-2));
  } else if (/^\d{1,2}$/.test(s)) {
    h = Number(s);
    mi = 0;
  } else {
    return null;
  }
  if (!Number.isInteger(h) || !Number.isInteger(mi) || h > 23 || mi > 59) return null;
  return `${pad2(h)}:${pad2(mi)}`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  return nbDateTime.format(new Date(iso));
}

export function formatRange(fromDate: string, toDate: string): string {
  if (fromDate === toDate) return formatDateLong(fromDate);
  const a = parseDate(fromDate);
  const b = parseDate(toDate);
  if (a.y === b.y && a.m === b.m) {
    return `${a.d}.–${formatDateLong(toDate)}`;
  }
  return `${formatDateLong(fromDate)} – ${formatDateLong(toDate)}`;
}

/** Kort, entydig norsk periode: «01.10.2026 – 02.10.2026» eller «02.10.2026». Uavhengig av nettleserspråk. */
export function formatRangeShort(fromDate: string, toDate: string): string {
  if (fromDate === toDate) return formatDateShort(fromDate);
  return `${formatDateShort(fromDate)} – ${formatDateShort(toDate)}`;
}

/** «2. okt.» – kompakt dagsetikett for akser og lister. */
export function formatDayCompact(date: string): string {
  const { d, m } = parseDate(date);
  const months = ['jan.', 'feb.', 'mars', 'apr.', 'mai', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'des.'];
  return `${d}. ${months[m - 1]}`;
}

/** 'HH:MM' Oslo-lokaltid for et UTC-tidspunkt, + dato (dd.mm.åååå) når dagen ikke er gitt. */
export function formatOsloTime(iso: string): string {
  return osloTimeOf(iso);
}

/** «02.10.2026» – Oslo-dagen et UTC-tidspunkt tilhører, i entydig norsk kortform. */
export function formatOsloDate(iso: string): string {
  return formatDateShort(osloDateOf(iso));
}

/** «02.10.2026 07:00–08:00» – tidsrom i Oslo-tid; dato gjentas kun når sluttdagen er en annen. */
export function formatOsloSpan(fromIso: string, toIso: string): string {
  const a = osloDateOf(fromIso);
  const b = osloDateOf(toIso);
  return `${formatDateShort(a)} ${osloTimeOf(fromIso)}–${a !== b ? `${formatDateShort(b)} ` : ''}${osloTimeOf(toIso)}`;
}

/**
 * Tolker brukerskrevet dato. Godtar «dd.mm.åååå», «d.m.åååå», «dd.mm.åå», «dd/mm/åååå» og ISO «åååå-mm-dd».
 * Returnerer 'YYYY-MM-DD' eller null. Tolker ALDRI amerikansk mm/dd.
 */
export function parseNbDate(input: string | null | undefined): string | null {
  if (!input) return null;
  const s = input.trim();
  if (isValidDate(s)) return s;
  const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/.exec(s);
  if (!m) return null;
  const d = Number(m[1]);
  const mo = Number(m[2]);
  let y = Number(m[3]);
  if (m[3].length === 2) y += 2000;
  const iso = `${y}-${pad2(mo)}-${pad2(d)}`;
  return isValidDate(iso) ? iso : null;
}

/** 'YYYY-MM-DD' → lokal JS Date (kl. 12 for å unngå DST-kanter). Til kalenderkomponenter. */
export function isoToLocalDate(date: string): Date {
  const { y, m, d } = parseDate(date);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/** Lokal JS Date → 'YYYY-MM-DD' (kalenderdato). */
export function localDateToIso(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
